import {
  getIndexPagination,
  getIndexPaginationSearchParams,
  normalizeIndexPagination,
  INDEX_PAGE_SIZES,
} from '@shared/lib/pagination';
import { Text } from '@mantine/core';
import { CellSort, EmptyRowsFallback } from '@shared/ui';
import { VacanciesTemplatesStatusBadge } from '../vacanciesTemplatesStatusBadge';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  type VacancyTemplate,
  useGetVacanciesTemplates,
} from '@entities/vacancy';
import { PaginateType, type SortQuery } from '@shared/types';
import { DataTable } from '@shared/ui/DataTable';

export const VacanciesTemplatesList = () => {
  const navigate = useNavigate();
  const [sort, setSort] = useState<SortQuery>({ id: 'desc' });

  const [searchParams, setSearchParams] = useSearchParams();

  const { page, pageSize } = getIndexPagination(searchParams);

  useEffect(() => {
    const normalized = normalizeIndexPagination(searchParams);
    if (normalized.toString() !== searchParams.toString()) {
      setSearchParams(normalized, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const setPage = (nextPage: number) => {
    setSearchParams(
      getIndexPaginationSearchParams(searchParams, nextPage, pageSize)
    );
  };
  const setPageSize = (size: number) => {
    setSearchParams(getIndexPaginationSearchParams(searchParams, 1, size));
  };

  const { data: vacanciesTemplatesData, isLoading } = useGetVacanciesTemplates({
    filter: {},
    sort,
    perPage: pageSize,
    page,
    paginate: PaginateType.DEFAULT,
  });

  const handleSortChange = (field: string, direction?: 'asc' | 'desc') => {
    if (!direction) {
      setSort({ id: 'desc' });
      return;
    }

    setSort({
      [field]: direction,
    });
  };

  const handleRowClick = ({ record }: { record: VacancyTemplate }) => {
    navigate(`/vacancies-templates-list/${record.id}/edit`);
  };

  const columns = useMemo(
    () => [
      {
        accessor: 'id',
        title: (
          <CellSort
            label='ID'
            onSort={dir => handleSortChange('id', dir)}
            currentDirection={sort.id}
          />
        ),
      },
      {
        accessor: 'name',
        title: (
          <CellSort
            label='Наименование'
            onSort={dir => handleSortChange('name', dir)}
            currentDirection={sort.name}
          />
        ),
      },
      {
        accessor: 'author',
        title: (
          <CellSort
            label='Автор'
            onSort={dir => handleSortChange('author.profile.fullName', dir)}
            currentDirection={sort['author.profile.fullName']}
          />
        ),
        render: ({ author }: VacancyTemplate) => (
          <Text size='sm'>
            {author?.profile.firstName || '-'} {author?.profile.lastName || ''}
          </Text>
        ),
      },
      {
        accessor: 'isActive',
        title: (
          <CellSort
            label='Статус'
            onSort={dir => handleSortChange('isActive', dir)}
            currentDirection={sort.isActive}
          />
        ),
        render: ({ isActive }: VacancyTemplate) => (
          <VacanciesTemplatesStatusBadge isActive={isActive} />
        ),
      },
    ],
    [sort, handleSortChange]
  );

  return (
    <DataTable<VacancyTemplate>
      fetching={isLoading}
      columns={columns}
      records={vacanciesTemplatesData?.data || []}
      onRowClick={handleRowClick}
      page={page}
      setPage={setPage}
      onRecordsPerPageChange={setPageSize}
      recordsPerPageOptions={INDEX_PAGE_SIZES}
      pageSize={pageSize}
      setPageSize={setPageSize}
      recordsPerPage={pageSize}
      onPageChange={setPage}
      totalRecords={vacanciesTemplatesData?.pagination.total}
      emptyState={
        <EmptyRowsFallback
          desc='Список шаблонов вакансий пуст'
          subDesc='Чтобы создать шаблон вакансии, нажмите кнопку «Создать шаблон»'
        />
      }
      highlightOnHover
    />
  );
};
